import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import './Widget.css';

interface SupportLevel {
  price: number;
  strength: number; // 1-10 scale
  lastTested: number;
  holdCount: number;
  breakCount: number;
  capitalAllocated: number;
}

interface SupportLevelData {
  currentPrice: number;
  levels: SupportLevel[];
  nextSupportPrice: number;
  nextResistancePrice: number;
  trendDirection: 'up' | 'down' | 'sideways';
}

export const SupportLevelStatus: React.FC = () => {
  const [supportData, setSupportData] = useState<SupportLevelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // This would come from a support-levels endpoint
        // For now, we'll derive from market data
        const marketData = await api.tradingEngine.getStatus();

        // In production, backend would calculate support/resistance levels
        // based on trading history and price action
        // Placeholder: show "-" for unavailable data
        setSupportData(null);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch support levels');
        console.error('Support level fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const formatUSD = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(value);
  };

  const getStrengthLabel = (strength: number): string => {
    if (strength >= 8) return 'Very Strong';
    if (strength >= 6) return 'Strong';
    if (strength >= 4) return 'Moderate';
    if (strength >= 2) return 'Weak';
    return 'Very Weak';
  };

  const getStrengthColor = (strength: number): string => {
    if (strength >= 8) return '#22c55e'; // green
    if (strength >= 6) return '#84cc16'; // lime
    if (strength >= 4) return '#eab308'; // yellow
    if (strength >= 2) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  const getTimeSinceTest = (timestamp: number): string => {
    if (timestamp === 0) return 'Never';
    const now = Date.now();
    const diff = now - timestamp;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days}d ${hours}h ago`;
    return `${hours}h ago`;
  };

  if (loading) {
    return (
      <div className="widget support-levels">
        <h3>Support Levels</h3>
        <div className="widget-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="widget support-levels">
        <h3>Support Levels</h3>
        <div className="widget-error">{error}</div>
      </div>
    );
  }

  // No support data available yet
  if (!supportData) {
    return (
      <div className="widget support-levels">
        <h3>Support Levels</h3>
        <div className="no-data-message">
          <div className="no-data-icon">📊</div>
          <p>Support level analysis will be available after sufficient trading history</p>
          <div className="info-text">
            Levels are calculated based on price action and successful accumulation points
          </div>
        </div>
      </div>
    );
  }

  const getTrendIcon = (direction: string): string => {
    if (direction === 'up') return '📈';
    if (direction === 'down') return '📉';
    return '➡️';
  };

  const getTrendLabel = (direction: string): string => {
    if (direction === 'up') return 'Uptrend';
    if (direction === 'down') return 'Downtrend';
    return 'Sideways';
  };

  return (
    <div className="widget support-levels">
      <div className="widget-header">
        <h3>Support Levels</h3>
        <div className="trend-indicator">
          <span className="trend-icon">{getTrendIcon(supportData.trendDirection)}</span>
          <span className="trend-label">{getTrendLabel(supportData.trendDirection)}</span>
        </div>
      </div>

      <div className="current-position">
        <div className="position-item">
          <label>Current Price</label>
          <div className="value">{formatUSD(supportData.currentPrice)}</div>
        </div>
        <div className="position-item">
          <label>Next Support</label>
          <div className="value support">{formatUSD(supportData.nextSupportPrice)}</div>
        </div>
        <div className="position-item">
          <label>Next Resistance</label>
          <div className="value resistance">{formatUSD(supportData.nextResistancePrice)}</div>
        </div>
      </div>

      <div className="support-list">
        <h4>Key Levels</h4>
        {supportData.levels.map((level, index) => {
          const isAbovePrice = level.price > supportData.currentPrice;
          const totalTests = level.holdCount + level.breakCount;
          const holdRate = totalTests > 0 ? (level.holdCount / totalTests) * 100 : 0;

          return (
            <div key={index} className={`support-level-item ${isAbovePrice ? 'resistance' : 'support'}`}>
              <div className="level-header">
                <span className="level-price">{formatUSD(level.price)}</span>
                <span className="level-type">{isAbovePrice ? 'Resistance' : 'Support'}</span>
              </div>

              <div className="level-strength">
                <div className="strength-label">{getStrengthLabel(level.strength)}</div>
                <div className="strength-bar">
                  <div
                    className="strength-fill"
                    style={{
                      width: `${(level.strength / 10) * 100}%`,
                      backgroundColor: getStrengthColor(level.strength),
                    }}
                  />
                </div>
                <div className="strength-value">{level.strength}/10</div>
              </div>

              <div className="level-stats">
                <div className="stat">
                  <label>Hold Rate</label>
                  <span className="value">{holdRate.toFixed(0)}%</span>
                </div>
                <div className="stat">
                  <label>Tests</label>
                  <span className="value">{totalTests}</span>
                </div>
                <div className="stat">
                  <label>Last Tested</label>
                  <span className="value">{getTimeSinceTest(level.lastTested)}</span>
                </div>
                <div className="stat">
                  <label>Capital</label>
                  <span className="value">{formatUSD(level.capitalAllocated)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="support-info">
        <div className="info-badge">
          <span className="info-icon">ℹ</span>
          <span className="info-text">
            Support levels strengthen with successful accumulation and increase buying power allocation
          </span>
        </div>
      </div>
    </div>
  );
};
